import { useCallback } from 'react';

import { hotspotCounterTriggered, selectedNonSequencedItem } from '../../../actions/contentItemActions';
import { GetItemByIdTyped, getItemByIdTypedSelector } from '../../../store/reducers/items/itemsSelectors';
import { addCompletedItem } from '../../../store/reducers/student/tracking/itemsTrackingActions';
import { useStudentSelector } from '../../../store/selectors/useStudentSelector';
import { StudentThunkDispatch } from '../../../types/redux';
import { isHotspotCompletedSelector } from '../../tracking/services/CompletionService';
import {
  ArrowPosition,
  ArrowType,
  FriendlyNameService,
  Hotspot,
  HotspotStyle,
  HotspotTab,
  IndicatorType,
  IPanorama,
  isContentEmpty,
  isHotspotTab,
  isPolygonalArrow,
  isSimpleArrow,
  KrpanoHotspot,
  PanoramaArrowIdentifier,
  PolygonalArrow,
  SimpleArrow,
} from '@ael/core-api';
import { useDispatch } from 'react-redux';


export const useHotspotMapFactory = (panorama: IPanorama) => {
  const dispatch = useDispatch();
  const getItemByIdTyped = useStudentSelector(getItemByIdTypedSelector);
  const isHotspotCompleted = useStudentSelector(isHotspotCompletedSelector);

  return useCallback(
      (hotspot: Hotspot) => {
        const isCompleted = isHotspotCompleted(hotspot);
        createPanoramaHotspot(panorama, hotspot, isCompleted, getItemByIdTyped, dispatch);
      },
      [dispatch, getItemByIdTyped, isHotspotCompleted, panorama]
  );
};

export function createPanoramaHotspot(
  globalPanorama: IPanorama,
  hotspot: Hotspot,
  isCompleted: boolean,
  getItemByIdTyped: GetItemByIdTyped,
  dispatch: StudentThunkDispatch
) {
  const emptyFirstTab: HotspotTab | undefined = getFirstHotspotTabIfIsOnlyOneAndIsEmpty(hotspot, getItemByIdTyped);

  const arrowId = FriendlyNameService.getFriendlyName(hotspot.id);
  const arrowTitleId = FriendlyNameService.getFriendlyName(hotspot.id + 'title');
  const onHotspotClick = () => {
    if (emptyFirstTab) {
      dispatch(hotspotCounterTriggered());
      if (isSimpleArrow(hotspot.position)) {
        createHotspotTitle(globalPanorama, hotspot.position, emptyFirstTab.title, arrowId, () =>
          dispatch(hotspotCounterTriggered())
        );
      }
      if (isPolygonalArrow(hotspot.position)) {
        if (!globalPanorama.isArrowRegistered(arrowTitleId)) {
          createPolygonalHotspotTitle(globalPanorama, hotspot.position, emptyFirstTab.title, arrowId);
          hotspotFinished(arrowId, hotspot.position, globalPanorama);
        } else {
          globalPanorama.removeArrow(arrowTitleId);
        }
      }

      if (!isCompleted) {
        dispatch(addCompletedItem(emptyFirstTab.id));
      }
    } else {
      dispatch(selectedNonSequencedItem(hotspot));
    }
  };

  globalPanorama.renderArrowToPano(
    hotspot.position,
    arrowId,
    {
      onClick: onHotspotClick,
      modifyHotspot: () => {
        if (isCompleted) {
          hotspotFinished(arrowId, hotspot.position, globalPanorama);
        }
      },
    },
    Math.random() * 500
  );
}

function createHotspotIcon(
  globalPanorama: IPanorama,
  simpleArrow: SimpleArrow,
  text: string,
  arrowId: PanoramaArrowIdentifier,
  onIconClick: () => void
) {
  globalPanorama.removeArrow(arrowId);
  const onClick = () => {
    onIconClick();
    createHotspotTitle(globalPanorama, simpleArrow, text, arrowId, onIconClick);
  };
  globalPanorama.renderArrowToPano(simpleArrow, arrowId, {
    onClick,
    modifyHotspot: (krpanoHotspot: KrpanoHotspot) => {
      krpanoHotspot.loadstyle(HotspotStyle.HotspotIconVisited);
    },
  });
}

function createHotspotTitle(
  globalPanorama: IPanorama,
  simpleArrow: SimpleArrow,
  text: string,
  arrowId: PanoramaArrowIdentifier,
  onIconClick: () => void
) {
  globalPanorama.removeArrow(arrowId);
  const onClick = () => {
    createHotspotIcon(globalPanorama, simpleArrow, text, arrowId, onIconClick);
  };
  globalPanorama.renderArrowToPano({ ...simpleArrow, arrowText: text }, arrowId, {
    onClick,
  });
}

function createPolygonalHotspotTitle(
  globalPanorama: IPanorama,
  polygonalArrow: PolygonalArrow,
  text: string,
  arrowId: PanoramaArrowIdentifier
) {
  const titleArrowId = FriendlyNameService.getFriendlyName(arrowId + 'title');
  const mousePosition = globalPanorama._pano.calculateMousePosition();
  const titleArrow: SimpleArrow = {
    arrowPositionConfiguration: {
      arrowPosition: new ArrowPosition(mousePosition.x, mousePosition.y),
      arrowPositionType: ArrowType.Simple,
    },
    fov: polygonalArrow.fov,
    sceneIndex: polygonalArrow.sceneIndex,
    arrowText: text,
    tourSceneGuid: globalPanorama.getActiveTourSceneGuidView(),
  };

  globalPanorama.renderArrowToPano(titleArrow, titleArrowId, {
    onClick: () => {
      globalPanorama.removeArrow(titleArrowId);
    },
  });
}

function getFirstHotspotTabIfIsOnlyOneAndIsEmpty(hotspot: Hotspot, getItemByIdTyped: GetItemByIdTyped) {
  if (hotspot.tabs.count() !== 1) {
    return undefined;
  }
  const firstTab = getItemByIdTyped(hotspot.tabs.first(), isHotspotTab);
  const hotspotIsEmpty = isContentEmpty(firstTab.content) && firstTab.audioAsset === null;
  return hotspotIsEmpty ? firstTab : undefined;
}

function hotspotFinished(
  arrowId: PanoramaArrowIdentifier,
  position: SimpleArrow | PolygonalArrow,
  panorama: IPanorama
) {
  const krpanoHotspot = panorama._pano.getHotspot(arrowId);

  if (position.arrowPositionConfiguration.arrowPositionType === ArrowType.Simple) {
    krpanoHotspot?.loadstyle(HotspotStyle.HotspotIconVisited);
  } else {
    let hotspotStyle = null;
    switch (position.arrowPositionConfiguration.arrowPosition.indicatorType) {
      case IndicatorType.PolygonalShape:
        hotspotStyle = HotspotStyle.HotspotPolygonalVisited;
        break;
      case IndicatorType.PolygonalWithIndicator: {
        hotspotStyle = HotspotStyle.HotspotPolygonalVisited;
        const indicator = panorama._pano.getHotspot(`${arrowId}-indicator`);
        indicator?.loadstyle(HotspotStyle.HotspotIndicatorVisited);
        break;
      }
      case IndicatorType.IndicatorOnly: {
        const indicatorOnly = panorama._pano.getHotspot(`${arrowId}-indicator`);
        indicatorOnly?.loadstyle(HotspotStyle.HotspotIndicatorVisited);
        break;
      }
      case IndicatorType.Invisible:
        break;
      default:
        hotspotStyle = HotspotStyle.HotspotPolygonalVisited;
        break;
    }
    if (hotspotStyle) {
      krpanoHotspot?.loadstyle(hotspotStyle);
    }
  }
}
